import FooterCTA from "@/components/landing/FooterCTA";
import Navbar from "@/components/landing/Navbar";
import StarsBackground from "@/components/landing/StarsBackground";

const Privacy = () => (
  <div className="min-h-screen bg-background relative">
    <StarsBackground />
    <div className="relative z-10">
      <Navbar />

      <section className="pt-24 pb-8 md:pt-28 md:pb-12">
        <div className="container-narrow text-center">
          <p className="text-xs font-medium uppercase tracking-widest text-accent mb-2">
            Privacy Policy
          </p>
          <p className="text-sm text-muted-foreground">Effective September 2, 2026</p>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="container-narrow max-w-3xl">
          <div className="surface-card p-6 md:p-8 space-y-6 body-md text-muted-foreground prose prose-invert prose-sm max-w-none">
            <p>
              This Privacy Policy explains how AntAtlas, Inc. collects, uses, and protects personal data
              when you visit antatlas.ai, join our waitlist, or use AntAtlas.
            </p>

            <h2 className="heading-md text-foreground pt-4">Who is responsible for your data</h2>
            <p>
              AntAtlas, Inc., a Delaware corporation, is the controller of personal data described in
              this policy.
            </p>
            <p>
              Contact: <a href="mailto:support@antatlas.ai">support@antatlas.ai</a>
            </p>

            <h2 className="heading-md text-foreground pt-4">Data we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-foreground">Waitlist data:</strong> the email address you submit.
              </li>
              <li>
                <strong className="text-foreground">Usage and technical data:</strong> page views,
                interactions, referring page, campaign parameters, browser and device information, IP
                address, and approximate location derived from it.
              </li>
              <li>
                <strong className="text-foreground">Local preferences:</strong> your theme preference,
                stored in your browser.
              </li>
            </ul>
            <p>
              AntAtlas does not store your source code or repository data. System graphs are generated
              and stored on the client side.
            </p>

            <h2 className="heading-md text-foreground pt-4">How and why we use personal data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                To manage the waitlist and send requested product updates, based on your consent. You may
                withdraw consent at any time.
              </li>
              <li>
                To operate, secure, troubleshoot, and improve the website, based on our legitimate
                interests in maintaining and understanding our service.
              </li>
              <li>To comply with law and protect our rights, where legally required.</li>
            </ul>
            <p>We do not sell personal data or use it for automated decisions with legal or similarly significant effects.</p>

            <h2 className="heading-md text-foreground pt-4">Service providers</h2>
            <p>We disclose personal data only as needed to providers that operate the service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Cloudflare</strong> for website hosting, delivery, and security.</li>
              <li><strong className="text-foreground">Resend</strong> for waitlist email delivery.</li>
              <li><strong className="text-foreground">Mixpanel</strong> for product and website analytics, when enabled.</li>
              <li>Professional advisers or authorities when required by law or necessary to protect legal rights.</li>
            </ul>
            <p>These providers may process data in countries outside your own. Where required, we use legally recognized transfer safeguards.</p>

            <h2 className="heading-md text-foreground pt-4">Cookies and browser storage</h2>
            <p>
              The website uses browser storage to remember your theme. If analytics is enabled, Mixpanel
              may use identifiers or similar technologies to measure visits and interactions. You can
              limit these technologies through your browser settings and supported privacy signals.
            </p>

            <h2 className="heading-md text-foreground pt-4">Retention</h2>
            <p>
              We retain your waitlist email until you unsubscribe, withdraw consent, or the waitlist is no
              longer needed. Analytics and security records are retained according to our configured
              provider retention periods and deleted or aggregated when no longer needed. We may retain
              limited records longer when required by law or to establish, exercise, or defend legal claims.
            </p>

            <h2 className="heading-md text-foreground pt-4">Your rights</h2>
            <p>
              Depending on where you live, you may have rights to access, correct, delete, restrict, or
              object to processing of your personal data, receive a portable copy, and withdraw consent.
              To exercise a right, email <a href="mailto:support@antatlas.ai">support@antatlas.ai</a>.
              We may need to verify your identity before completing a request.
            </p>
            <p>
              If you are in the EEA, you may also complain to your local supervisory authority. Our local
              authority is Portugal&apos;s Comissão Nacional de Proteção de Dados at{" "}
              <a href="https://www.cnpd.pt/" target="_blank" rel="noreferrer">cnpd.pt</a>.
            </p>

            <h2 className="heading-md text-foreground pt-4">Security</h2>
            <p>
              We use reasonable technical and organizational measures to protect personal data. No method
              of transmission or storage is completely secure, so we cannot guarantee absolute security.
            </p>

            <h2 className="heading-md text-foreground pt-4">Children</h2>
            <p>AntAtlas is intended for business users and is not directed to children under 16.</p>

            <h2 className="heading-md text-foreground pt-4">Changes and contact</h2>
            <p>
              We may update this policy as the service changes. We will post the revised policy here and
              update its effective date. Questions and privacy requests can be sent to{" "}
              <a href="mailto:support@antatlas.ai">support@antatlas.ai</a>.
            </p>
          </div>
        </div>
      </section>

      <FooterCTA />
    </div>
  </div>
);

export const head = {
  title: "Privacy Policy — AntAtlas",
  description: "How AntAtlas, Inc. collects, uses, and protects personal data.",
  path: "/privacy",
};

export default Privacy;
